import { TFunction } from 'i18next';
import { DataLogStatus } from 'store/app/enums';
import { User } from 'store/auth/models';
import { MappedReturn } from 'store/returns/models/returnData';

export enum ReturnActionLabelKey {
  OPEN = 'open',
  CONTINUE = 'continue',
  REVIEW = 'review',
  CREATE = 'create',
}

export type ReturnActionRecord = Pick<MappedReturn, 'status' | 'dataLog'>;

export type ReturnActionDataLog = {
  status?: string;
  reviewerUser?: User | null;
  clientUser?: User | null;
};

/** Build a return-action record from an entity submission row (VatReturnLog). */
export function toReturnActionRecord(
  dataLog: ReturnActionDataLog
): ReturnActionRecord {
  return {
    status:
      DataLogStatus[dataLog.status as keyof typeof DataLogStatus] ??
      dataLog.status ??
      '',
    dataLog: dataLog as ReturnActionRecord['dataLog'],
  };
}

/** Action button width on the Returns list view. */
export const RETURN_ACTION_BUTTON_WIDTH = '90px';

/** Action button width on the entity Tax Returns tab. */
export const RETURN_ACTION_BUTTON_WIDTH_ENTITY = '80px';

export const RETURN_ACTION_TRANSLATION_KEYS: Record<
  ReturnActionLabelKey,
  string
> = {
  [ReturnActionLabelKey.OPEN]: 'main.open',
  [ReturnActionLabelKey.CONTINUE]: 'common.continue',
  [ReturnActionLabelKey.REVIEW]: 'common.review',
  [ReturnActionLabelKey.CREATE]: 'main.create',
};

export function normalizeReturnStatus(status?: string): string {
  if (!status) {
    return DataLogStatus.NOT_STARTED;
  }

  const enumValue = DataLogStatus[status as keyof typeof DataLogStatus];
  return enumValue || status;
}

export function isReturnAwaitingReviewByUser(
  record: ReturnActionRecord,
  currentUser?: User
): boolean {
  if (!record.dataLog || !currentUser?.userUuid) {
    return false;
  }

  const status = normalizeReturnStatus(record.status);
  if (status === DataLogStatus.CANCELLED) {
    return false;
  }

  if (
    status === DataLogStatus.AWAITING_APPROVAL &&
    record.dataLog.reviewerUser?.userUuid === currentUser.userUuid
  ) {
    return true;
  }

  if (
    status === DataLogStatus.AWAITING_CLIENT_APPROVAL &&
    record.dataLog.clientUser?.userUuid === currentUser.userUuid
  ) {
    return true;
  }

  return false;
}

export function isReturnAwaitingReviewStatus(
  record: ReturnActionRecord
): boolean {
  const status = normalizeReturnStatus(record.status);
  return (
    status === DataLogStatus.AWAITING_APPROVAL ||
    status === DataLogStatus.AWAITING_CLIENT_APPROVAL
  );
}

export function getReturnActionLabelKey(
  record: ReturnActionRecord,
  currentUser?: User
): ReturnActionLabelKey {
  if (isReturnAwaitingReviewByUser(record, currentUser)) {
    return ReturnActionLabelKey.REVIEW;
  }

  if (normalizeReturnStatus(record.status) === DataLogStatus.SUBMITTED) {
    return ReturnActionLabelKey.OPEN;
  }

  if (isReturnAwaitingReviewStatus(record)) {
    return ReturnActionLabelKey.OPEN;
  }

  if (normalizeReturnStatus(record.status) === DataLogStatus.NOT_STARTED) {
    return ReturnActionLabelKey.CREATE;
  }

  return ReturnActionLabelKey.CONTINUE;
}

export function getReturnActionTranslationKey(
  labelKey: ReturnActionLabelKey
): string {
  return RETURN_ACTION_TRANSLATION_KEYS[labelKey];
}

export function getReturnActionLabel(
  record: ReturnActionRecord,
  t: TFunction,
  currentUser?: User
): string {
  const labelKey = getReturnActionLabelKey(record, currentUser);
  return t(getReturnActionTranslationKey(labelKey));
}

/** Whether clicking the action should start a new submission (no active data log). */
export function shouldCreateNewSubmission(record: ReturnActionRecord): boolean {
  if (!record.dataLog) {
    return true;
  }

  return normalizeReturnStatus(record.status) === DataLogStatus.CANCELLED;
}

/**
 * Whether the return action control should be shown.
 * On PWA, create-new-submission actions are hidden (view/edit only).
 */
export function shouldShowReturnAction(
  record: ReturnActionRecord,
  options?: { allowCreate?: boolean }
): boolean {
  if (options?.allowCreate === false && shouldCreateNewSubmission(record)) {
    return false;
  }
  return true;
}

/** Filled (primary) styling for Create, Saved, and Review returns. */
export function isReturnActionPrimary(
  record: ReturnActionRecord,
  currentUser?: User
): boolean {
  if (normalizeReturnStatus(record.status) === DataLogStatus.SUBMITTED) {
    return false;
  }

  if (isReturnAwaitingReviewByUser(record, currentUser)) {
    return true;
  }

  if (isReturnAwaitingReviewStatus(record)) {
    return false;
  }

  return (
    shouldCreateNewSubmission(record) ||
    normalizeReturnStatus(record.status) === DataLogStatus.SAVED
  );
}
